import React, { useState } from "react";
import { usePricingSettings } from "../../contexts/PricingSettingsContext";
import { apiFetch } from "../../lib/apiFetch";

export function OfferHelperPanel({ evalData, onSelectCounter, product, onUseAiMessage }) {
  const [aiCoach, setAiCoach] = useState(null);
  const [aiLoading, setAiLoading] = useState(false);
  const [aiError, setAiError] = useState(null);
  const { pricingSettings } = usePricingSettings();

  if (!evalData || !evalData.ok) return null;

  const badge =
    evalData.classification === "great"
      ? "text-green-300"
      : evalData.classification === "fair"
      ? "text-amber-300"
      : "text-red-300";
  const aiEnabled = pricingSettings?.enableOfferAi !== false;

  async function handleAskOfferAi() {
    if (!aiEnabled) return;
    setAiLoading(true);
    setAiError(null);
    try {
      const context = {
        productName: product?.title || product?.name || "",
        quantity: evalData.quantity,
        offerAmount: evalData.offerAmount,
        totalCost: evalData.totalCost,
        margin: evalData.offerMargin,
        classification: evalData.classification,
        targetMargin: evalData.targetMargin ?? pricingSettings?.targetWholesaleMargin ?? null,
        minHourlyRate: evalData.minHourlyRate ?? pricingSettings?.minHourlyRate ?? null,
        studioName: product?.studioName || "",
      };
      const res = await apiFetch("/api/ai/coach", {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ mode: "offer", context }),
      });
      const json = await res.json();
      if (!json.ok) throw new Error(json.error || "AI error");
      setAiCoach(json.result);
    } catch (err) {
      console.error("[OfferHelperPanel] AI error", err);
      setAiError("Mudo AI couldn’t review this offer right now.");
    } finally {
      setAiLoading(false);
    }
  }

  return (
    <div className="mudo-card p-4 text-sm space-y-3">
      <div className="flex items-center justify-between">
        <h3 className="mudo-section-title">Offer evaluation</h3>
        <span className={`text-xs ${badge} capitalize`}>{evalData.classification} offer</span>
      </div>

      <div className="text-xs opacity-80 space-y-1">
        <div>Offer amount: ${evalData.offerAmount}</div>
        <div>True cost: ${evalData.totalCost.toFixed(2)}</div>
        <div>Margin at this offer: {Math.round(evalData.offerMargin * 100)}%</div>
      </div>

      <div className="text-xs opacity-70 pt-1">Suggested counter offers:</div>

      <div className="space-y-2">
        {evalData.suggestedCounters.map((c, idx) => (
          <button
            key={idx}
            onClick={() => onSelectCounter?.(c.amount)}
            className="w-full text-left text-xs px-3 py-2 rounded-xl border border-white/10 bg-black/40 hover:bg-black/60"
          >
            {c.label}
          </button>
        ))}
      </div>

      <div className="border border-dashed border-cyan-500/40 rounded-xl px-3 py-2 text-[11px] mt-2 space-y-1">
        <div className="flex items-center justify-between">
          <span className="uppercase tracking-wide opacity-70">Mudo AI</span>
          <button
            type="button"
            onClick={handleAskOfferAi}
            disabled={aiLoading || !aiEnabled}
            className="text-[11px] px-2 py-0.5 rounded-full border border-white/10 bg-black/40 hover:bg-black/60 disabled:opacity-50 disabled:cursor-not-allowed"
          >
            {aiLoading ? "Thinking…" : "Explain this offer"}
          </button>
        </div>
        {aiError && <p className="text-[11px] text-red-300 mt-1">{aiError}</p>}
        {aiCoach && !aiError && (
          <div className="mt-1 space-y-1">
            <div className="font-medium opacity-80">{aiCoach.title}</div>
            <p className="opacity-80">{aiCoach.summary}</p>
            {Array.isArray(aiCoach.bullets) && aiCoach.bullets.length > 0 && (
              <ul className="list-disc list-inside opacity-80 space-y-0.5">
                {aiCoach.bullets.map((b, idx) => (
                  <li key={idx}>{b}</li>
                ))}
              </ul>
            )}
            {aiCoach.counterMessage && (
              <div className="mt-2 rounded-xl border border-white/10 bg-black/40 px-3 py-2 space-y-2">
                <div className="opacity-70 mb-1">Suggested message:</div>
                <div className="whitespace-pre-wrap opacity-90 text-[11px]">
                  {aiCoach.counterMessage}
                </div>
                {onUseAiMessage && (
                  <button
                    type="button"
                    onClick={() => onUseAiMessage(aiCoach.counterMessage)}
                    className="text-[11px] px-3 py-1.5 rounded-full border border-white/10 bg-black/50 hover:bg-black/70"
                  >
                    Use this message
                  </button>
                )}
              </div>
            )}
          </div>
        )}
      </div>
    </div>
  );
}
